/**
 * Client for the AutoMom skill-checkpoint prompts API.
 *
 * Raised when a skill's learning tasks for a level complete. The user is
 * offered three choices:
 *  - confirm      → the skill's current_level advances (evidence: self-report)
 *                   and any journey gated on the skill is reconciled
 *  - need_more    → keep practicing; level unchanged
 *  - ask_for_help → "gap too big tonight"; routes to first-lesson + help
 *
 * Mirrors goalReviewService. Routes are gated server-side by
 * JOURNEY_SKILL_THRESHOLDS_ENABLED, so this returns empty when off.
 */

const BASE = '/api/automom/v1/skills'

export interface SkillCheckpointPrompt {
  id: string
  skill_id: number
  skill_title: string
  plan_id: number | null
  prerequisite_step_ref: string | null
  current_level: string | null
  unlocks_level: string | null
  target_level: string | null
  status: 'available' | 'shown' | 'responded' | 'dismissed'
  response_code: string | null
  expires_at: string
  created_at: string
}

export type SkillCheckpointResponseCode = 'confirm' | 'need_more' | 'ask_for_help'

export interface SkillCheckpointRespondResult {
  success: boolean
  response_code: SkillCheckpointResponseCode
  skill_id: number
  level_advanced?: boolean
  new_level?: string | null
  next_step: 'level_advanced' | 'keep_practicing' | 'first_lesson_and_help' | null
  error?: string
}

async function getCheckpoints(): Promise<{
  prompts: SkillCheckpointPrompt[]
  prompt: SkillCheckpointPrompt | null
}> {
  const res = await fetch(`${BASE}/checkpoints`, { credentials: 'include' })
  if (!res.ok) return { prompts: [], prompt: null }
  const data = await res.json()
  return { prompts: data.prompts ?? [], prompt: data.prompt ?? null }
}

async function markShown(promptId: string): Promise<void> {
  await fetch(`${BASE}/checkpoints/${promptId}/shown`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined)
}

async function respond(
  promptId: string,
  responseCode: SkillCheckpointResponseCode,
): Promise<SkillCheckpointRespondResult> {
  const res = await fetch(`${BASE}/checkpoints/${promptId}/respond`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ response_code: responseCode }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    return {
      success: false,
      response_code: responseCode,
      skill_id: 0,
      next_step: null,
      error: (data && data.error) || `http_${res.status}`,
    }
  }
  return data as SkillCheckpointRespondResult
}

export const skillCheckpointService = { getCheckpoints, markShown, respond }
